import React from "react";
import { useRouter } from "@tanstack/react-router";
import { useOwnPatientDetail } from "../contexts/OwnPatientDetailContext";
import {
  usePatientGetIdentityUrls,
  usePaymentGetProfileStatus,
} from "./api/generated/sASOnlineClinicBackendAPI";
import { resolveIdentityRegistrationStatus } from "../features/patient/identityRegistration";
import { isGmoPaymentEnabled } from "./featureFlags";
import { hasPaymentFeatureDisabledMarker } from "./payment/paymentFeatureDisabled";
import { getStatus } from "../utils/http/getStatus";

type RedirectTarget = "/patient/home" | "/forbidden" | "/server-error";

export function ReservationEligibilityGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  const gmoPaymentEnabled = isGmoPaymentEnabled();
  const router = useRouter();
  const {
    patientId,
    statusCpap,
    cpapOnlineReservationAllowed,
    isLoading: patientLoading,
    hasError: patientHasError,
    errorStatus: patientErrorStatus,
  } = useOwnPatientDetail();

  const identityUrlsQuery = usePatientGetIdentityUrls(patientId ?? "", {
    query: {
      enabled: Boolean(patientId) && !patientLoading && !patientHasError,
    },
  });

  const paymentProfileQuery = usePaymentGetProfileStatus({
    query: {
      enabled:
        gmoPaymentEnabled &&
        Boolean(patientId) &&
        !patientLoading &&
        !patientHasError,
    },
  });

  const redirectTo = React.useMemo<RedirectTarget | null>(() => {
    if (patientLoading) return null;
    if (patientHasError) {
      if (!patientErrorStatus || patientErrorStatus >= 500) {
        return "/server-error";
      }
      if (patientErrorStatus === 403) {
        return "/forbidden";
      }
      return "/patient/home";
    }
    if (!patientId) {
      return "/patient/home";
    }
    if (statusCpap === "診療1回目予約依頼") {
      return "/patient/home";
    }
    if (statusCpap === "診療予約依頼" && !cpapOnlineReservationAllowed) {
      return "/patient/home";
    }
    if (
      identityUrlsQuery.isLoading ||
      (gmoPaymentEnabled && paymentProfileQuery.isLoading)
    )
      return null;

    if (identityUrlsQuery.error) {
      const status = getStatus(identityUrlsQuery.error);
      if (!status || status >= 500) {
        return "/server-error";
      }
      if (status === 403) {
        return "/forbidden";
      }
      return "/patient/home";
    }

    if (!identityUrlsQuery.data) return null;

    if (identityUrlsQuery.data.status === 403) {
      return "/forbidden";
    }

    if (identityUrlsQuery.data.status !== 200) {
      return "/patient/home";
    }

    const { canReserve } = resolveIdentityRegistrationStatus(
      identityUrlsQuery.data.data,
    );
    if (!canReserve) return "/patient/home";

    // TODO(sas-online): 暫定対応として、UNREGISTERED のみ予約ブロック対象外とする。
    // 正式対応では予約可否と請求方法の整合をバックエンド返却値に基づいて判定し直す。
    if (!gmoPaymentEnabled) {
      return null;
    }

    if (paymentProfileQuery.error) {
      const status = getStatus(paymentProfileQuery.error);
      if (!status || status >= 500) {
        return "/server-error";
      }
      if (status === 403) {
        return "/forbidden";
      }
      return "/patient/home";
    }

    if (!paymentProfileQuery.data) return null;

    if (paymentProfileQuery.data.status === 200) {
      const data = paymentProfileQuery.data.data as any;
      if (hasPaymentFeatureDisabledMarker(data?.requiredActions)) {
        return null;
      }
    } else {
      return "/patient/home";
    }

    return null;
  }, [
    patientLoading,
    patientHasError,
    patientErrorStatus,
    patientId,
    statusCpap,
    cpapOnlineReservationAllowed,
    identityUrlsQuery.isLoading,
    identityUrlsQuery.error,
    identityUrlsQuery.data,
    paymentProfileQuery.isLoading,
    paymentProfileQuery.error,
    paymentProfileQuery.data,
    gmoPaymentEnabled,
  ]);

  React.useEffect(() => {
    if (!redirectTo) return;
    router.navigate({ to: redirectTo, replace: true });
  }, [redirectTo, router]);

  if (redirectTo) return null;
  if (
    patientLoading ||
    identityUrlsQuery.isLoading ||
    (gmoPaymentEnabled && paymentProfileQuery.isLoading)
  )
    return null;

  return <>{children}</>;
}
